import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import EntityTimeline from "@/components/pms/EntityTimeline";
import DocumentsTab from "@/components/pms/DocumentsTab";
import GovtIdUploader from "@/components/GovtIdUploader";
import { AdvancePaymentsCard } from "@/components/pms/AdvancePaymentsCard";
import { CheckInDialog } from "@/components/pms/CheckInDialog";
import { GroupCheckInPanel } from "@/components/pms/GroupCheckInPanel";
import { AssignRoomButton } from "@/components/pms/AssignRoomButton";
import { AssignRoomDialog } from "@/components/pms/AssignRoomDialog";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { openPdf, reservationRegCardUrl, voucherUrl } from "@/lib/documents";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import { ArrowLeft, Pencil, Save, X, History, FileText, CheckCircle2, AlertTriangle, LogIn, XCircle, Printer, Ticket, Users } from "lucide-react";
import { useCheckinGate, GateWrap } from "@/hooks/useCheckinGate";

function Section({ title, children, onEdit, editing, onSave, onCancel }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500">{title}</h3>
        {!editing ? (
          <Button size="sm" variant="ghost" onClick={onEdit}><Pencil className="w-3 h-3"/></Button>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onCancel}><X className="w-3 h-3"/></Button>
            <Button size="sm" onClick={onSave}><Save className="w-3 h-3 mr-1"/>Save</Button>
          </div>
        )}
      </div>
      {children}
    </Card>
  );
}

function Row({ label, value, mono = true }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-200 last:border-0">
      <div className="text-[11px] mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] text-slate-800 text-right max-w-[60%] truncate ${mono ? "mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [res, setRes] = useState(null);
  const [editing, setEditing] = useState({});
  const [form, setForm] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [versions, setVersions] = useState([]);
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [sources, setSources] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [pickerDiag, setPickerDiag] = useState(null);
  const [assignRoomDialog, setAssignRoomDialog] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const location = useLocation();
  const [tab, setTab] = useState(new URLSearchParams(location.search).get("tab") === "group" ? "group" : "overview");
  useEffect(() => { if (new URLSearchParams(location.search).get("tab") === "group") setTab("group"); }, [location.search]);

  const load = useCallback(async () => {
    try {
      const [r, t, v] = await Promise.all([
        api.get(`/pms/reservations/${id}`),
        api.get(`/pms/reservations/${id}/timeline`),
        api.get(`/pms/reservations/${id}/versions`),
      ]);
      setRes(r.data); setForm(r.data); setTimeline(t.data.items); setVersions(v.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try { const s = await api.get("/master/booking-sources/?page_size=50"); setSources(s.data.items); }
      catch { /* noop */ }
    })();
  }, []);

  // Load rooms available for this hotel — used by both the edit dialog room
  // selector AND the "Assign room & check-in" flow when a reservation is
  // confirmed without a pre-assigned room. `include_dirty=true` gives the
  // operator the full assignable pool (vacant-clean AND vacant-dirty) —
  // housekeeping preps dirty rooms before arrival. We also pass the
  // reservation's own dates so the API can exclude rooms pre-assigned to
  // overlapping active reservations (real double-book protection).
  const loadAvailableRooms = useCallback(async (hotelId, opts = {}) => {
    if (!hotelId) return;
    const buildParams = (withTypeFilter) => {
      const p = new URLSearchParams({
        limit: "200", include_dirty: "true", hotel_id: hotelId,
      });
      if (opts.arrival_date) p.set("arrival_date", opts.arrival_date);
      if (opts.departure_date) p.set("departure_date", opts.departure_date);
      if (opts.exclude_reservation_id) p.set("exclude_reservation_id", opts.exclude_reservation_id);
      if (withTypeFilter && opts.room_type_id) p.set("room_type_id", opts.room_type_id);
      return p;
    };
    try {
      // 1st attempt — with room_type_id filter if the reservation specifies one.
      let { data } = await api.get(`/inventory/available?${buildParams(true).toString()}`);
      // Fallback — if the type filter zeroed out the picker but the hotel
      // does have rooms available across other types, drop the filter and
      // let the operator pick from the full pool (front desk can honour
      // an upgrade in one click instead of Edit → change type → re-open).
      if ((data.items || []).length === 0 && opts.room_type_id) {
        const wider = await api.get(`/inventory/available?${buildParams(false).toString()}`);
        if ((wider.data.items || []).length > 0) {
          data = wider.data;
          data.items = data.items.map(it => ({
            ...it,
            _upgrade: it.room_type_id !== opts.room_type_id,
          }));
          toast.info("No matching room type available — showing all rooms. Guest will be upgraded/switched.");
        }
      }
      setAvailableRooms(data.items || []);
      setPickerDiag(data.diagnostics || null);
      // If STILL empty, surface the diagnostic breakdown so the failure is
      // never silent at the front desk.
      const diag = data.diagnostics;
      if ((data.items || []).length === 0 && diag && diag.total_rooms_in_hotel > 0) {
        const parts = [];
        if (diag.excluded?.occupied) parts.push(`${diag.excluded.occupied} occupied`);
        if (diag.excluded?.reserved_overlap) parts.push(`${diag.excluded.reserved_overlap} reserved`);
        if (diag.excluded?.oos_ooo_blocked) parts.push(`${diag.excluded.oos_ooo_blocked} OOO/OOS/blocked`);
        if (diag.excluded?.inactive_or_deleted) parts.push(`${diag.excluded.inactive_or_deleted} inactive/deleted`);
        if (parts.length) toast.info(`No rooms available — all ${diag.total_rooms_in_hotel} rooms filtered: ${parts.join(", ")}`);
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => { if (res?.hotel_id) loadAvailableRooms(res.hotel_id); }, [res?.hotel_id, loadAvailableRooms]);

  const saveSection = async (fields) => {
    const payload = {};
    fields.forEach(f => { if (form[f] !== res[f]) payload[f] = form[f]; });
    if (!Object.keys(payload).length) { setEditing({}); return; }
    try {
      const { data } = await api.patch(`/pms/reservations/${id}/modify`, payload);
      if (data.decision === "REQUIRE_APPROVAL") {
        setApprovalDialog(data);
        toast.info("Modification requires approval");
      } else if (data.decision === "ALLOW") {
        toast.success(`Updated (v${data.version_no})`);
      }
      setEditing({});
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg && msg.decision === "BLOCK") toast.error(`Blocked: ${msg.reason}`);
      else toast.error(formatApiError(e));
    }
  };

  const cancel = async () => {
    try {
      await api.post(`/pms/reservations/${id}/cancel`, { reason: cancelReason || "Cancelled" });
      toast.success("Reservation cancelled");
      setCancelDialog(false); setCancelReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const gate = useCheckinGate();
  const gated = gate.isBlocked(res?.arrival_date);
  const isGroup = !!(res?.is_multi_room && (res?.rooms || []).length > 1);
  const docsAllowed = res && !["cancelled", "no_show"].includes(res.status);
  const checkIn = async () => {
    if (gated) { toast.error(gate.reason(res.arrival_date)); return; }
    // GROUP-CHECKIN · multi-room bookings check in room by room (or all at once) from the Group tab.
    if (isGroup) { setTab("group"); return; }
    // If no room assigned yet, pick one first (ASSIGN-ROOM dialog), then continue into the check-in dialog.
    if (!res.assigned_room_id) {
      setAssignRoomDialog(true);
      return;
    }
    setCheckInOpen(true);
  };

  const openEditDialog = async () => {
    // Seed form with the current reservation + guest details.
    const seed = {
      arrival_date: (res.arrival_date || "").slice(0, 16),
      departure_date: (res.departure_date || "").slice(0, 16),
      estimated_arrival_time: res.estimated_arrival_time || "",
      adults: res.adults || 1,
      children: res.children || 0,
      infants: res.infants || 0,
      booking_source: res.booking_source || "direct",
      booking_type: res.booking_type || "individual",
      assigned_room_id: res.assigned_room_id || "",
      room_rate_minor: res.room_rate_minor || 0,
      notes: res.notes || "",
      special_requests: res.special_requests || "",
      breakfast_included: !!res?.stay_benefits?.meal_plan?.includes_breakfast,
      // Guest fields (populated from linked guest doc)
      salutation: "",
      first_name: "", last_name: "", primary_phone: "", email: "",
      govt_id_type: "passport", govt_id_number: "",
      nationality: "", govt_id_document_url: "",
    };
    try {
      if (res.guest_id) {
        const { data: g } = await api.get(`/pms/guests/${res.guest_id}`);
        Object.assign(seed, {
          salutation: g.salutation || g.title_code || "",
          first_name: g.first_name || "",
          last_name: g.last_name || "",
          primary_phone: g.primary_phone || "",
          email: g.primary_email || g.email || "",
          govt_id_type: g.govt_id_type || "passport",
          govt_id_number: g.govt_id_number || "",
          nationality: g.nationality || "",
          govt_id_document_url: g.govt_id_document_url || "",
        });
      }
    } catch { /* keep the reservation-only defaults */ }
    setEditForm(seed);
    setEditDialog(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // 1. Reservation-level changes → PATCH /modify
      const resPatch = {};
      const cmpFields = ["arrival_date", "departure_date", "estimated_arrival_time",
                          "adults", "children", "infants", "booking_source", "booking_type",
                          "assigned_room_id", "room_rate_minor", "notes", "special_requests"];
      cmpFields.forEach(f => {
        if (editForm[f] !== undefined && editForm[f] !== (res[f] ?? (typeof editForm[f] === "number" ? 0 : ""))) {
          resPatch[f] = editForm[f];
        }
      });
      // Breakfast — send updated stay_benefits when toggled
      const wasBreakfast = !!res?.stay_benefits?.meal_plan?.includes_breakfast;
      if (editForm.breakfast_included !== wasBreakfast) {
        if (editForm.breakfast_included) {
          const a = new Date(editForm.arrival_date || res.arrival_date);
          const d = new Date(editForm.departure_date || res.departure_date);
          // BB semantics: breakfast served (arrival+1 day) … departure (inclusive).
          const dates = [];
          const cur = new Date(a.getFullYear(), a.getMonth(), a.getDate() + 1);
          const end = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          while (cur <= end) { dates.push(cur.toISOString().slice(0, 10)); cur.setDate(cur.getDate() + 1); }
          resPatch.stay_benefits = {
            meal_plan: {
              kind: "bb", includes_breakfast: true, includes_lunch: false, includes_dinner: false,
              adults_covered: Number(editForm.adults) || 0, children_covered: Number(editForm.children) || 0,
              valid_breakfast_dates: dates, valid_lunch_dates: [], valid_dinner_dates: [],
            },
          };
        } else {
          resPatch.stay_benefits = null;
        }
      }
      if (Object.keys(resPatch).length) {
        const { data } = await api.patch(`/pms/reservations/${id}/modify`, resPatch);
        if (data.decision === "REQUIRE_APPROVAL") {
          setApprovalDialog(data);
        }
      }
      // 2. Guest-level changes → PATCH /pms/guests/{id}
      if (res.guest_id) {
        const gp = {};
        ["first_name", "last_name", "primary_phone", "govt_id_type", "govt_id_number",
         "nationality", "govt_id_document_url"].forEach(f => {
          if (editForm[f] !== undefined && editForm[f] !== "") gp[f] = editForm[f];
        });
        // Title (salutation) — send even when cleared so it can be removed.
        if (editForm.salutation !== undefined) gp.salutation = editForm.salutation || null;
        if (editForm.email) gp.primary_email = editForm.email;
        if (Object.keys(gp).length) {
          try { await api.patch(`/pms/guests/${res.guest_id}`, gp); }
          catch (e) { toast.error(`Guest update: ${formatApiError(e)}`); }
        }
      }
      toast.success("Reservation updated");
      setEditDialog(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg && msg.decision === "BLOCK") toast.error(`Blocked: ${msg.reason}`);
      else toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!res) return <div className="text-hocp-textMuted">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="reservation-detail-page">
      <PageHeader
        title={`Reservation ${res.code || id?.slice(-8).toUpperCase() || ""}`}
        subtitle={`${res.guest_name} · ${res.adults}A ${res.children || 0}C`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2"/>Back</Button>
            {res.status !== "cancelled" && (
              <Button onClick={openEditDialog} data-testid="edit-reservation-button">
                <Pencil className="w-4 h-4 mr-2"/>Edit
              </Button>
            )}
            {!isGroup && (res.status === "confirmed" || res.status === "pending") && (
              <div className="flex items-center gap-2 rounded-md border border-hocp-border bg-white px-3 h-10" data-testid="res-room-control">
                <span className="text-[11px] mono uppercase tracking-wider text-hocp-textMuted">Room</span>
                <AssignRoomButton reservation={res} onAssigned={load} stopPropagation={false} />
              </div>
            )}
            {docsAllowed && (
              <div className="flex items-center gap-1" data-testid="regcard-actions">
                <Button variant="outline" onClick={() => requestRegCard({ url: reservationRegCardUrl(id), filename: `RegistrationCard-${res.code}.pdf` })} data-testid="print-regcard-button">
                  <Printer className="w-4 h-4 mr-2"/>Registration Card
                </Button>
                <DownloadPdfButton url={reservationRegCardUrl(id)} filename={`RegistrationCard-${res.code}.pdf`} label="Download registration card" iconOnly size="lg" regCard className="h-10 w-10 justify-center px-0" testId="download-regcard-button" />
              </div>
            )}
            {docsAllowed && (
              <div className="flex items-center gap-1" data-testid="voucher-actions">
                <Button variant="outline" onClick={() => openPdf(voucherUrl(id), `BookingVoucher-${res.code}.pdf`)} data-testid="print-voucher-button">
                  <Ticket className="w-4 h-4 mr-2"/>Booking Voucher
                </Button>
                <DownloadPdfButton url={voucherUrl(id)} filename={`BookingVoucher-${res.code}.pdf`} label="Download booking voucher" iconOnly size="lg" className="h-10 w-10 justify-center px-0" testId="download-voucher-button" />
              </div>
            )}
            {(res.status === "confirmed" || res.status === "pending" || (isGroup && res.status === "checked_in" && res.partial_checkin)) && (
              <GateWrap blocked={gated} reason={gate.reason(res.arrival_date)} testid="checkin-gate">
                <Button onClick={checkIn} disabled={gated} data-testid="checkin-button">
                  {isGroup ? <Users className="w-4 h-4 mr-2"/> : <LogIn className="w-4 h-4 mr-2"/>}
                  {isGroup ? "Group Check-in" : res.assigned_room_id ? "Check-in" : "Assign Room & Check-in"}
                </Button>
              </GateWrap>
            )}
            {res.status !== "cancelled" && res.status !== "checked_in" && (
              <Button variant="outline" onClick={() => setCancelDialog(true)} data-testid="cancel-button"><XCircle className="w-4 h-4 mr-2"/>Cancel</Button>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-2">
        <Badge tone={res.status === "confirmed" ? "info" : res.status === "checked_in" ? "success" : res.status === "cancelled" ? "error" : res.status === "checked_out" ? "neutral" : "warning"} data-testid="res-detail-status">{res.status === "checked_out" ? "Checked Out" : res.status === "checked_in" && res.partial_checkin ? "Partially checked in" : res.status}</Badge>
        {isGroup && <Badge tone="neutral" data-testid="res-group-badge"><Users className="w-3 h-3 mr-1 inline" />{res.rooms.length} rooms</Badge>}
        <Badge tone="neutral">{res.booking_source}</Badge>
        <span className="text-[11px] mono text-hocp-textMuted">Created {(res.created_at || "").slice(0, 16).replace("T", " ")}</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          {isGroup && <TabsTrigger value="group" data-testid="tab-group"><Users className="w-3 h-3 mr-1"/>Group Check-In ({res.rooms.length})</TabsTrigger>}
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions"><History className="w-3 h-3 mr-1"/>Versions ({versions.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-3 h-3 mr-1"/>Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Dates"
              editing={editing.dates}
              onEdit={() => setEditing({ dates: true })}
              onCancel={() => { setEditing({}); setForm(res); }}
              onSave={() => saveSection(["arrival_date", "departure_date", "estimated_arrival_time"])}>
              {editing.dates ? (
                <div className="space-y-2">
                  <div><Label>Arrival</Label><DatePicker value={(form.arrival_date || "").slice(0, 10)} onChange={(v) => setForm({ ...form, arrival_date: v })}/></div>
                  <div><Label>Departure</Label><DatePicker value={(form.departure_date || "").slice(0, 10)} onChange={(v) => setForm({ ...form, departure_date: v })} minDate={form.arrival_date}/></div>
                  <div><Label>ETA</Label><Input value={form.estimated_arrival_time || ""} onChange={(e) => setForm({ ...form, estimated_arrival_time: e.target.value })}/></div>
                </div>
              ) : (
                <>
                  <Row label="Arrival" value={(res.arrival_date || "").slice(0, 16).replace("T", " ")}/>
                  <Row label="Departure" value={(res.departure_date || "").slice(0, 16).replace("T", " ")}/>
                  <Row label="ETA" value={res.estimated_arrival_time}/>
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-200 last:border-0" data-testid="res-room-row">
                    <div className="text-[11px] mono uppercase tracking-wider text-slate-500">Room</div>
                    <div className="text-[13px] text-slate-800 text-right">
                      {isGroup ? <span className="mono">{res.rooms.map(l => l.room_number).filter(Boolean).join(", ")}</span>
                        : <AssignRoomButton reservation={res} onAssigned={load} stopPropagation={false} /> }
                      {!isGroup && !res.room_number && !(res.status === "confirmed" || res.status === "pending") && "—"}
                    </div>
                  </div>
                </>
              )}
            </Section>

            <Section title="Pax"
              editing={editing.pax}
              onEdit={() => setEditing({ pax: true })}
              onCancel={() => { setEditing({}); setForm(res); }}
              onSave={() => saveSection(["adults", "children", "infants"])}>
              {editing.pax ? (
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Adults</Label><Input type="number" value={form.adults} onChange={(e) => setForm({ ...form, adults: +e.target.value })}/></div>
                  <div><Label>Children</Label><Input type="number" value={form.children || 0} onChange={(e) => setForm({ ...form, children: +e.target.value })}/></div>
                  <div><Label>Infants</Label><Input type="number" value={form.infants || 0} onChange={(e) => setForm({ ...form, infants: +e.target.value })}/></div>
                </div>
              ) : (
                <>
                  <Row label="Adults" value={res.adults}/>
                  <Row label="Children" value={res.children || 0}/>
                  <Row label="Infants" value={res.infants || 0}/>
                </>
              )}
            </Section>

            <Section title="Booking"
              editing={editing.booking}
              onEdit={() => setEditing({ booking: true })}
              onCancel={() => { setEditing({}); setForm(res); }}
              onSave={() => saveSection(["booking_source", "booking_type"])}>
              {editing.booking ? (
                <div className="space-y-2">
                  <div><Label>Source</Label>
                    <Select value={form.booking_source} onValueChange={(v) => setForm({ ...form, booking_source: v })}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>{sources.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Type</Label><Input value={form.booking_type} onChange={(e) => setForm({ ...form, booking_type: e.target.value })}/></div>
                </div>
              ) : (
                <>
                  <Row label="Source" value={res.booking_source}/>
                  <Row label="Type" value={res.booking_type}/>
                  <Row label="Code" value={res.code}/>
                </>
              )}
            </Section>

            <Section title="Notes / Special Requests"
              editing={editing.notes}
              onEdit={() => setEditing({ notes: true })}
              onCancel={() => { setEditing({}); setForm(res); }}
              onSave={() => saveSection(["notes", "special_requests"])}>
              {editing.notes ? (
                <div className="space-y-2">
                  <div>
                    <Label className="text-[11px] mono uppercase">Notes</Label>
                    <Input value={form.notes || ""} placeholder="e.g. Late arrival expected" onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
                  </div>
                  <div>
                    <Label className="text-[11px] mono uppercase">Special requests</Label>
                    <Input value={form.special_requests || ""} placeholder="e.g. High floor, extra pillows" onChange={(e) => setForm({ ...form, special_requests: e.target.value })}/>
                  </div>
                </div>
              ) : (
                <>
                  <Row label="Notes" value={res.notes} mono={false}/>
                  <Row label="Special" value={res.special_requests} mono={false}/>
                </>
              )}
            </Section>
            <AdvancePaymentsCard reservation={res} onChanged={load} />
          </div>
        </TabsContent>

        {isGroup && <TabsContent value="group" className="mt-4"><GroupCheckInPanel reservationId={id} onChanged={load} /></TabsContent>}
        <TabsContent value="timeline"><EntityTimeline items={timeline}/></TabsContent>

        <TabsContent value="versions">
          <div className="space-y-3">
            {versions.length === 0 && <div className="text-hocp-textMuted text-sm py-6 text-center">No version history yet</div>}
            {versions.map((v, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-amber-700">v{v.version_no}</span>
                    <Badge tone={v.status === "applied" ? "success" : v.status === "approved" ? "success" : v.status === "rejected" ? "error" : "warning"}>{v.status}</Badge>
                  </div>
                  <span className="text-[11px] mono text-slate-500">{(v.changed_at || "").slice(0, 19).replace("T", " ")} · {v.changed_by}</span>
                </div>
                <div className="text-[12px] text-slate-700 space-y-1">
                  {(v.changed_fields || []).map(f => (
                    <div key={f} className="grid grid-cols-3 gap-2 mono">
                      <span className="text-slate-500">{f}</span>
                      <span className="text-red-700 line-through truncate">{String(v.previous_values?.[f] ?? "—")}</span>
                      <span className="text-emerald-700 truncate">→ {String(v.new_values?.[f] ?? "—")}</span>
                    </div>
                  ))}
                </div>
                {v.rule_code && <div className="mt-2 text-[11px] mono text-slate-500">Rule: {v.rule_code}</div>}
                {v.workflow_instance_id && <div className="text-[11px] mono text-sky-700">Workflow: {v.workflow_instance_id}</div>}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents"><DocumentsTab entityType="reservation" entityId={id}/></TabsContent>
      </Tabs>

      <Dialog open={!!approvalDialog} onOpenChange={(v) => !v && setApprovalDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approval Required</DialogTitle></DialogHeader>
          <div className="space-y-2 text-[13px]">
            <div>This modification needs manager approval per rule <span className="mono font-semibold text-amber-700">{approvalDialog?.rule_code}</span>.</div>
            <div>Workflow instance: <span className="mono font-semibold text-sky-700">{approvalDialog?.workflow_instance_id}</span></div>
            <div>Version snapshot: <span className="mono">v{approvalDialog?.version_no}</span></div>
          </div>
          <DialogFooter>
            <Button onClick={() => setApprovalDialog(null)}><CheckCircle2 className="w-4 h-4 mr-2"/>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel Reservation?</DialogTitle></DialogHeader>
          <div>
            <Label className="text-[11px] mono uppercase" htmlFor="rd-cancel-reason">Cancellation reason</Label>
            <Input id="rd-cancel-reason" placeholder="e.g. Guest requested cancellation" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason" className="mt-1"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Back</Button>
            <Button onClick={cancel} data-testid="confirm-cancel"><AlertTriangle className="w-4 h-4 mr-2"/>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── ASSIGN-ROOM · pick a room, then (when launched from Check-in) continue straight into the check-in dialog ── */}
      <AssignRoomDialog reservation={res} open={assignRoomDialog} onOpenChange={setAssignRoomDialog}
        onAssigned={async () => { await load(); if (res.status === "confirmed" || res.status === "pending") setCheckInOpen(true); }} />

      {/* ── Edit Reservation dialog ────────────────────────────────── */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Reservation · {res.code}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2" data-testid="edit-reservation-dialog">
            {/* Guest block */}
            <div>
              <div className="text-[11px] mono uppercase tracking-wider text-slate-500 font-semibold mb-2">Guest</div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Title</Label>
                  <Select value={editForm.salutation || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, salutation: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="edit-title"><SelectValue placeholder="Select title"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Mx"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="hidden sm:block"/>
                <div><Label>First name</Label><Input value={editForm.first_name || ""} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} data-testid="edit-first-name"/></div>
                <div><Label>Last name</Label><Input value={editForm.last_name || ""} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}/></div>
                <div><Label>Phone</Label><Input value={editForm.primary_phone || ""} onChange={(e) => setEditForm({ ...editForm, primary_phone: e.target.value })}/></div>
                <div><Label>Email</Label><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}/></div>
                <div><Label>Govt ID type</Label>
                  <Select value={editForm.govt_id_type || "passport"} onValueChange={(v) => setEditForm({ ...editForm, govt_id_type: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["passport", "aadhaar", "drivers_license", "other"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Govt ID number</Label><Input value={editForm.govt_id_number || ""} onChange={(e) => setEditForm({ ...editForm, govt_id_number: e.target.value })}/></div>
                <div><Label>Nationality</Label><Input value={editForm.nationality || ""} placeholder="e.g. Indian, British, US" onChange={(e) => setEditForm({ ...editForm, nationality: e.target.value })} data-testid="edit-nationality"/></div>
              </div>
              <div className="mt-2">
                <Label>Govt ID document</Label>
                <GovtIdUploader value={editForm.govt_id_document_url}
                                 onChange={(url) => setEditForm({ ...editForm, govt_id_document_url: url })}
                                 testid="edit-idupload"/>
              </div>
            </div>

            {/* Stay block */}
            <div>
              <div className="text-[11px] mono uppercase tracking-wider text-slate-500 font-semibold mb-2">Stay</div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Arrival</Label><DatePicker value={(editForm.arrival_date || "").slice(0, 10)} onChange={(v) => setEditForm({ ...editForm, arrival_date: v })}/></div>
                <div><Label>Departure</Label><DatePicker value={(editForm.departure_date || "").slice(0, 10)} onChange={(v) => setEditForm({ ...editForm, departure_date: v })} minDate={editForm.arrival_date}/></div>
                <div className="col-span-2">
                  <Label>Room</Label>
                  <Select value={editForm.assigned_room_id || "__none__"}
                          onValueChange={(v) => setEditForm({ ...editForm, assigned_room_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="edit-assigned-room"><SelectValue placeholder="— None (assign later) —"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None (assign later) —</SelectItem>
                      {/* Keep the currently-assigned room in the list even if the
                          snapshot no longer shows it as available, so the operator
                          doesn't lose their selection. */}
                      {editForm.assigned_room_id && !availableRooms.some(r => r.room_id === editForm.assigned_room_id || r.id === editForm.assigned_room_id) && (
                        <SelectItem value={editForm.assigned_room_id}>
                          {res.assigned_room_number || editForm.assigned_room_id} · current
                        </SelectItem>
                      )}
                      {availableRooms.map(r => (
                        <SelectItem key={r.room_id || r.id} value={r.room_id || r.id}>
                          Room {r.room_number} · {r.room_type_name || r.room_type_code || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Adults</Label><Input type="number" min={1} value={editForm.adults ?? 1} onChange={(e) => setEditForm({ ...editForm, adults: +e.target.value })}/></div>
                <div><Label>Children</Label><Input type="number" min={0} value={editForm.children ?? 0} onChange={(e) => setEditForm({ ...editForm, children: +e.target.value })}/></div>
                <div><Label>Infants</Label><Input type="number" min={0} value={editForm.infants ?? 0} onChange={(e) => setEditForm({ ...editForm, infants: +e.target.value })}/></div>
                <div><Label>Nightly rate (minor)</Label><Input type="number" min={0} value={editForm.room_rate_minor ?? 0} onChange={(e) => setEditForm({ ...editForm, room_rate_minor: +e.target.value })}/></div>
                <div><Label>Source</Label>
                  <Select value={editForm.booking_source || "direct"} onValueChange={(v) => setEditForm({ ...editForm, booking_source: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{sources.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Type</Label><Input value={editForm.booking_type || ""} onChange={(e) => setEditForm({ ...editForm, booking_type: e.target.value })}/></div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <div className="text-[11px] mono uppercase tracking-wider text-slate-500 font-semibold mb-2">Notes</div>
              <Label className="text-[11px] mono uppercase text-slate-600">Notes</Label>
              <Input placeholder="e.g. Late arrival expected" value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mb-2 mt-1"/>
              <Label className="text-[11px] mono uppercase text-slate-600">Special requests</Label>
              <Input placeholder="e.g. High floor, extra pillows" value={editForm.special_requests || ""} onChange={(e) => setEditForm({ ...editForm, special_requests: e.target.value })} className="mt-1"/>
            </div>

            {/* Breakfast toggle */}
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
              <div>
                <Label className="text-[11px] mono uppercase text-slate-600">Breakfast Included?</Label>
                <div className="text-[11px] text-slate-500 mt-0.5">Sync to the F&amp;B Breakfast Register for every stay night.</div>
              </div>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant={editForm.breakfast_included ? "default" : "outline"} onClick={() => setEditForm({ ...editForm, breakfast_included: true })} data-testid="edit-breakfast-yes" className="h-8 px-3">Yes</Button>
                <Button type="button" size="sm" variant={!editForm.breakfast_included ? "default" : "outline"} onClick={() => setEditForm({ ...editForm, breakfast_included: false })} data-testid="edit-breakfast-no" className="h-8 px-3">No</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-reservation-save">
              <Save className="w-4 h-4 mr-2"/>{saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CheckInDialog reservationId={id} open={checkInOpen} onOpenChange={setCheckInOpen}
        onDone={(data) => { if (data?.stay_id) navigate(`/pms/stays/${data.stay_id}`); else load(); }} />

    </div>
  );
}
